import { Breadcrumb as AntBreadcrumb, Button, Flex } from "antd";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import ArrowLeftIcon from "../assets/icons/ArrowLeftIcon";
import ChevronRightIcon from "../assets/icons/ChevronRightIcon";
import { cn } from "../utils/global.utils";

interface BreadcrumbProps {
  items: { title: ReactNode | string }[];
  showBackButton?: boolean;
  onBack?: () => void;
}

interface TitleProps {
  icon?: ReactNode;
  title?: string | ReactNode;
  description?: string;
  titleClassName?: string;
}

interface CustomTitleProps extends TitleProps {
  items?: { title: ReactNode | string }[];
  children?: ReactNode;
  showBackButton?: boolean;
  onBack?: () => void;
}

// ------ Breadcrumb Component ------
const Breadcrumb = ({
  items,
  showBackButton = true,
  onBack,
}: BreadcrumbProps) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  return (
    <Flex align="center" gap={16}>
      {showBackButton && (
        <Button
          className="btn-bordered pl-2 pr-3"
          size="small"
          onClick={handleBack}
        >
          <ArrowLeftIcon /> Back
        </Button>
      )}

      <AntBreadcrumb
        separator={<ChevronRightIcon />}
        rootClassName="flex items-center"
        items={items || []}
      />
    </Flex>
  );
};

// ------ Title Component ------
const Title: React.FC<TitleProps> = ({
  icon,
  title,
  description,
  titleClassName,
}) =>
  title ? (
    <div>
      {!!title && (
        <Flex gap={6} align="center">
          {icon && <span>{icon}</span>}
          <h4
            className={cn("text-2xl font-bold text-zinc-700", titleClassName)}
          >
            {title}
          </h4>
        </Flex>
      )}
      {description && (
        <p className="text-base text-slate-600 !font-normal mt-1">
          {description}
        </p>
      )}
    </div>
  ) : null;

// ------ CustomTitle Component ------
const CustomTitle = ({
  title,
  description,
  icon,
  items,
  children,
  showBackButton,
  onBack,
}: CustomTitleProps) => (
  <div className="mb-3 space-y-6">
    {items?.length ? (
      <Breadcrumb
        items={items}
        showBackButton={showBackButton}
        onBack={onBack}
      />
    ) : null}

    <Flex align="center" justify="space-between" gap={24}>
      <Title
        icon={icon}
        title={title}
        description={description}
        titleClassName="text-xl"
      />
      {children && (
        <Flex align="center" gap={12}>
          {children}
        </Flex>
      )}
    </Flex>
  </div>
);

// Attach sub components for modular access
CustomTitle.Title = Title;
CustomTitle.Breadcrumb = Breadcrumb;

export default CustomTitle;
